import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Check,
  CheckCheck,
  ChevronRight,
  ExternalLink,
  Loader2,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/atoms/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import {
  useNotificationAnnouncement,
  useNotificationUnreadCount,
} from '@/stores/notification.store';
import { useAuthPermissions } from '@/stores/auth.store';
import { ApiError } from '@/lib/api/errors';
import {
  INTERNAL_MESSAGE_NOTIFICATION_TYPE,
  notificationActionUrl,
  shouldOpenNotificationGroup,
} from '@/lib/internal-chat-notification-target';
import { useNotificationStream } from '@/hooks/notifications/useNotificationStream';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationGroupItems,
  useNotificationsList,
  useNotificationUnreadQuery,
} from '@/hooks/notifications/useStaffNotifications';
import { formatDateTimeInTimeZone, getViewerTimeZone } from '@/lib/date-values';
import { cn } from '@/lib/utils';
import {
  NOTIFICATION_PRIORITY_META,
  NOTIFICATION_PRIORITY_ORDER,
  type NotificationPriority,
  type NotificationPriorityMeta,
  type NotificationGroupItem,
  type StaffNotification,
} from '@/types/notifications';

type PanelFilter = 'action' | 'attention' | 'messages' | 'all';

interface NotificationRailButtonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NotificationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NotificationPanelProps {
  onClose: () => void;
}

export function NotificationRuntime() {
  const permissions = useAuthPermissions();
  const canViewNotifications = permissions.includes('notifications:view');
  const announcement = useNotificationAnnouncement();
  useNotificationStream({ enabled: canViewNotifications });
  useNotificationUnreadQuery({ enabled: canViewNotifications });

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}

export function NotificationRailButton({ open, onOpenChange }: NotificationRailButtonProps) {
  const permissions = useAuthPermissions();
  const canViewNotifications = permissions.includes('notifications:view');
  const unread = useNotificationUnreadCount();

  if (!canViewNotifications) return null;

  return (
    <Button
      type="button"
      variant={open ? 'default' : 'ghost'}
      size="icon"
      className="relative size-10 rounded-lg"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      aria-controls="global-notification-panel"
      aria-expanded={open}
      onClick={() => onOpenChange(!open)}
    >
      <Bell className="size-5" />
      {unread > 0 ? (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </Button>
  );
}

export function NotificationSheet({ open, onOpenChange }: NotificationSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        id="global-notification-panel"
        side="right"
        showCloseButton={false}
        className="w-full gap-0 p-0 sm:max-w-[520px]"
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">Notifications</SheetTitle>
        <NotificationPanel onClose={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const navigate = useNavigate();
  const permissions = useAuthPermissions();
  const canViewNotifications = permissions.includes('notifications:view');
  const unread = useNotificationUnreadCount();
  const [filter, setFilter] = useState<PanelFilter>('all');
  const [selectedGroup, setSelectedGroup] = useState<StaffNotification | null>(null);
  const actionListQuery = useNotificationsList({
    unreadOnly: true,
    priority: 'action',
    excludeEventType: INTERNAL_MESSAGE_NOTIFICATION_TYPE,
    enabled: canViewNotifications,
    limit: 50,
  });
  const reviewListQuery = useNotificationsList({
    unreadOnly: true,
    priority: 'attention',
    excludeEventType: INTERNAL_MESSAGE_NOTIFICATION_TYPE,
    enabled: canViewNotifications,
    limit: 50,
  });
  const messagesListQuery = useNotificationsList({
    eventType: INTERNAL_MESSAGE_NOTIFICATION_TYPE,
    enabled: canViewNotifications,
    limit: 50,
  });
  const allListQuery = useNotificationsList({
    enabled: canViewNotifications,
    limit: 50,
  });
  const listQuery =
    filter === 'action'
      ? actionListQuery
      : filter === 'attention'
        ? reviewListQuery
        : filter === 'messages'
          ? messagesListQuery
          : allListQuery;
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const groupQuery = useNotificationGroupItems(selectedGroup?.recipientId ?? null, {
    enabled: selectedGroup !== null,
    // Opening an unread group marks it read immediately. Keep the drill-down
    // stable across that mutation by loading the whole non-archived group.
    status: 'all',
    firstSeenAt: selectedGroup?.firstSeenAt,
    lastEventAt: selectedGroup?.lastEventAt,
  });
  const actionItems = useMemo(
    () => actionListQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [actionListQuery.data],
  );
  const reviewItems = useMemo(
    () => reviewListQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [reviewListQuery.data],
  );
  const allItems = useMemo(
    () => allListQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [allListQuery.data],
  );
  const messageItems = useMemo(
    () => messagesListQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [messagesListQuery.data],
  );
  const items = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [listQuery.data],
  );
  const actionCount = actionItems.length;
  const reviewCount = reviewItems.length;
  const actionCountLabel = `${actionCount}${actionListQuery.hasNextPage ? '+' : ''}`;
  const reviewCountLabel = `${reviewCount}${reviewListQuery.hasNextPage ? '+' : ''}`;
  const messageCountLabel = `${messageItems.length}${messagesListQuery.hasNextPage ? '+' : ''}`;
  const allCountLabel = `${allItems.length}${allListQuery.hasNextPage ? '+' : ''}`;
  const groupedItems = useMemo(() => groupNotificationsByPriority(items), [items]);
  const headerTitle =
    filter === 'messages'
      ? `${messageCountLabel} team ${messageItems.length === 1 ? 'message' : 'messages'}`
      : actionCount > 0
        ? `${actionCountLabel} ${actionCount === 1 ? 'thing needs' : 'things need'} you`
        : unread > 0
          ? `${unread} unread`
          : 'All caught up';

  if (!canViewNotifications) return null;

  const openTarget = (item: StaffNotification) => {
    if (item.readAt === null && !markRead.isPending) {
      markRead.mutate(item.recipientId);
    }
    // Older deployments and entity-only deduplication can return groupCount
    // without concrete group items. Drill down only for operational group keys;
    // otherwise preserve the notification's direct action.
    if (shouldOpenNotificationGroup(item)) {
      setSelectedGroup(item);
      return;
    }
    openNotificationTarget(notificationActionUrl(item), navigate, onClose);
  };

  const markItemRead = (item: StaffNotification) => {
    if (item.readAt !== null) return;
    markRead.mutate(item.recipientId);
  };

  if (selectedGroup) {
    return (
      <NotificationGroupPanel
        group={selectedGroup}
        query={groupQuery}
        onBack={() => setSelectedGroup(null)}
        onClose={onClose}
        onOpen={(actionUrl, target) =>
          openNotificationTarget(
            notificationActionUrl({
              type: selectedGroup.type,
              actionUrl,
              entityType: target.entityType,
              entityId: target.entityId,
            }),
            navigate,
            onClose,
          )
        }
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
      <header className="border-b px-4 pb-0 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Notifications
            </p>
            <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight">{headerTitle}</h2>
          </div>
          <div className="flex items-center">
            <Button type="button" variant="ghost" size="icon" className="size-8" asChild>
              <Link
                to="/dashboard/settings/notifications"
                aria-label="Tune notification settings"
                onClick={onClose}
              >
                <SlidersHorizontal className="size-4" />
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Close notifications"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex items-end gap-6" aria-label="Notification filters">
          {(
            [
              ['action', 'Needs you', actionCountLabel],
              ['attention', 'Review', reviewCountLabel],
              ['messages', 'Messages', messageCountLabel],
              ['all', 'All', allCountLabel],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key as PanelFilter)}
              className={cn(
                'relative pb-2.5 text-[13px] font-semibold transition-colors',
                filter === key ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
              aria-pressed={filter === key}
            >
              {label} <span className="ml-1 text-muted-foreground">{count}</span>
              {filter === key ? (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-foreground" />
              ) : null}
            </button>
          ))}
        </div>
      </header>

      <div className="flex items-center justify-between border-b px-4 py-2 text-[11px] text-muted-foreground">
        <span>
          {filter === 'messages'
            ? 'Team message notifications'
            : `${unread} unread · grouped by urgency`}
        </span>
        <Link
          to="/dashboard/settings/notifications"
          onClick={onClose}
          className="font-semibold text-success hover:underline"
        >
          Tune
        </Link>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {listQuery.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading notifications
          </div>
        ) : listQuery.isError ? (
          <NotificationPanelError onRetry={() => void listQuery.refetch()} />
        ) : items.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {filter === 'action'
              ? 'Nothing needs action'
              : filter === 'attention'
                ? 'Nothing waiting for review'
                : filter === 'messages'
                  ? 'No team message notifications'
                  : 'No notifications'}
          </div>
        ) : (
          <div>
            {NOTIFICATION_PRIORITY_ORDER.map((priority) => {
              const sectionItems = groupedItems[priority];
              if (sectionItems.length === 0) return null;
              return (
                <NotificationPrioritySection
                  key={priority}
                  priority={priority}
                  items={sectionItems}
                  markingRecipientId={markRead.isPending ? markRead.variables : null}
                  onOpen={openTarget}
                  onRead={markItemRead}
                />
              );
            })}
            {listQuery.hasNextPage ? (
              <div className="p-3 text-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={listQuery.isFetchingNextPage}
                  onClick={() => void listQuery.fetchNextPage()}
                >
                  {listQuery.isFetchingNextPage ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : null}
                  Load older
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <footer className="flex items-center justify-between border-t bg-muted/20 px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-start"
          onClick={() => markAll.mutate()}
          disabled={unread === 0 || markAll.isPending}
        >
          {markAll.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <CheckCheck className="size-3.5" />
          )}
          Mark all read
        </Button>
        <Button type="button" variant="ghost" size="sm" asChild>
          <Link to="/dashboard/notifications" onClick={onClose}>
            History
            <ExternalLink className="size-3.5" />
          </Link>
        </Button>
      </footer>
    </div>
  );
}

function NotificationGroupPanel({
  group,
  query,
  onBack,
  onClose,
  onOpen,
}: {
  group: StaffNotification;
  query: ReturnType<typeof useNotificationGroupItems>;
  onBack: () => void;
  onClose: () => void;
  onOpen: (
    actionUrl: string | null,
    target: Pick<NotificationGroupItem, 'entityType' | 'entityId'>,
  ) => void;
}) {
  const meta = NOTIFICATION_PRIORITY_META[group.priority];
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  useEffect(() => {
    if (
      query.isError &&
      query.error instanceof ApiError &&
      query.error.status === 404 &&
      isSafeNotificationActionUrl(group.actionUrl)
    ) {
      onOpen(group.actionUrl, {
        entityType: group.entityType,
        entityId: group.entityId,
      });
    }
  }, [group.actionUrl, group.entityId, group.entityType, onOpen, query.error, query.isError]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
      <header className="border-b px-3 py-3">
        <div className="grid grid-cols-[32px_minmax(0,1fr)_32px] items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Back to notifications"
            onClick={onBack}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div className="min-w-0">
            <p
              className={cn(
                'font-mono text-[9px] font-semibold uppercase tracking-[0.17em]',
                meta.sectionClass,
              )}
            >
              {meta.sectionTitle} · {group.groupCount} events
            </p>
            <h2 className="mt-0.5 truncate text-base font-semibold tracking-tight">
              {group.title}
            </h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Close notifications"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-2 border-b bg-muted/15 px-4 py-2 text-[11px] text-muted-foreground">
        <span className={cn('size-1.5 rounded-full', meta.dotClass)} />
        <span>Each row is one specific notification.</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading group details
          </div>
        ) : query.isError ? (
          <NotificationPanelError onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            No individual notifications found
          </div>
        ) : (
          <div className="divide-y">
            {items.map((item) => (
              <NotificationGroupItemRow
                key={item.id}
                item={item}
                meta={meta}
                onOpen={() => onOpen(item.actionUrl, item)}
              />
            ))}
            {query.hasNextPage ? (
              <div className="p-3 text-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  Load older
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {isSafeNotificationActionUrl(group.actionUrl) ? (
        <footer className="border-t bg-muted/20 p-3">
          <Button
            type="button"
            className="w-full"
            onClick={() =>
              onOpen(group.actionUrl, {
                entityType: group.entityType,
                entityId: group.entityId,
              })
            }
          >
            {notificationActionLabel(group)}
            <ArrowRight className="size-4" />
          </Button>
        </footer>
      ) : null}
    </div>
  );
}

function NotificationGroupItemRow({
  item,
  meta,
  onOpen,
}: {
  item: NotificationGroupItem;
  meta: NotificationPriorityMeta;
  onOpen: () => void;
}) {
  const canOpen = isSafeNotificationActionUrl(item.actionUrl);
  return (
    <div className="grid grid-cols-[4px_minmax(0,1fr)_28px] gap-3 px-4 py-3">
      <span className={cn('h-full min-h-10 rounded-full opacity-65', meta.railClass)} />
      <button
        type="button"
        className="min-w-0 text-left disabled:cursor-default"
        disabled={!canOpen}
        onClick={onOpen}
      >
        <p className="truncate text-[13px] font-semibold">{item.title}</p>
        <p className="mt-0.5 line-clamp-2 text-xs leading-[1.3rem] text-muted-foreground">
          {item.body}
        </p>
        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
          {formatWhen(item.occurredAt)}
          {canOpen ? ' · open item' : ''}
        </p>
      </button>
      {canOpen ? (
        <Button type="button" variant="ghost" size="icon" className="size-7" onClick={onOpen}>
          <ChevronRight className="size-4" />
          <span className="sr-only">Open {item.title}</span>
        </Button>
      ) : null}
    </div>
  );
}

function NotificationPanelError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div className="max-w-xs space-y-3">
        <p className="text-sm font-medium">Notifications could not load</p>
        <p className="text-xs text-muted-foreground">Retry when the connection is available.</p>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

function NotificationPrioritySection({
  priority,
  items,
  markingRecipientId,
  onOpen,
  onRead,
}: {
  priority: NotificationPriority;
  items: StaffNotification[];
  markingRecipientId: string | null | undefined;
  onOpen: (item: StaffNotification) => void;
  onRead: (item: StaffNotification) => void;
}) {
  const meta = NOTIFICATION_PRIORITY_META[priority];

  return (
    <section aria-label={meta.sectionTitle} className="border-b last:border-b-0">
      <NotificationSectionHeader meta={meta} count={items.length} />
      <div className="divide-y">
        {items.map((item) => (
          <NotificationLedgerRow
            key={item.recipientId}
            item={item}
            meta={meta}
            compact={priority === 'fyi'}
            marking={markingRecipientId === item.recipientId}
            onOpen={() => onOpen(item)}
            onRead={() => onRead(item)}
          />
        ))}
      </div>
    </section>
  );
}

function NotificationSectionHeader({
  meta,
  count,
}: {
  meta: NotificationPriorityMeta;
  count: number;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 bg-muted/15 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]',
        meta.sectionClass,
      )}
    >
      <span>{meta.sectionTitle}</span>
      <span className="h-px flex-1 bg-border" />
      <span className="text-muted-foreground">{count}</span>
    </div>
  );
}

function NotificationLedgerRow({
  item,
  meta,
  compact,
  marking,
  onOpen,
  onRead,
}: {
  item: StaffNotification;
  meta: NotificationPriorityMeta;
  compact: boolean;
  marking: boolean;
  onOpen: () => void;
  onRead: () => void;
}) {
  const unread = item.readAt === null;
  const hasSafeAction = isSafeNotificationActionUrl(item.actionUrl);

  return (
    <div
      className={cn(
        'grid grid-cols-[36px_minmax(0,1fr)_auto] gap-2.5 px-4',
        compact ? 'py-2.5' : 'py-3',
      )}
    >
      <div className="pt-0.5 text-center">
        <span
          className={cn(
            'block font-mono text-xl leading-none tracking-tight',
            compact && 'text-base',
          )}
        >
          {item.groupCount}
        </span>
        <span className={cn('mx-auto mt-1.5 block size-1.5 rounded-full', meta.dotClass)} />
      </div>
      <div className="min-w-0">
        <button type="button" className="block w-full min-w-0 text-left" onClick={onOpen}>
          <p className={cn('truncate font-semibold', compact ? 'text-xs' : 'text-[13px]')}>
            {item.title}
          </p>
          <p
            className={cn(
              'mt-0.5 text-muted-foreground',
              compact ? 'line-clamp-1 text-[11px]' : 'line-clamp-2 text-xs leading-[1.35rem]',
            )}
          >
            {item.body}
          </p>
        </button>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {hasSafeAction ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground hover:underline"
              onClick={onOpen}
            >
              {notificationActionLabel(item)}
              {compact ? (
                <ChevronRight className="size-3.5" />
              ) : (
                <ArrowRight className="size-3.5" />
              )}
            </button>
          ) : null}
          <span className="text-[11px] text-muted-foreground">{formatNotificationWhen(item)}</span>
        </div>
      </div>
      <NotificationReadButton marking={marking} unread={unread} onRead={onRead} compact />
    </div>
  );
}

function NotificationReadButton({
  marking,
  unread,
  onRead,
  compact = false,
}: {
  marking: boolean;
  unread: boolean;
  onRead: () => void;
  compact?: boolean;
}) {
  if (!unread) return <span className={compact ? 'size-7' : 'size-8'} />;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={compact ? 'size-7' : 'size-8'}
            disabled={marking}
            onClick={onRead}
            aria-label="Mark notification as read"
          >
            {marking ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className={compact ? 'size-3.5' : 'size-4'} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Mark as read</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function notificationActionLabel(item: StaffNotification): string {
  if (item.groupCount > 1) {
    const actionUrl = item.actionUrl ?? '';
    if (actionUrl.startsWith('/dashboard/calendar')) return 'Open calendar';
    if (actionUrl.startsWith('/dashboard/dispatch')) return 'Open dispatch';
    if (actionUrl.startsWith('/dashboard/leads')) return 'Open leads';
    if (actionUrl.startsWith('/dashboard/payments')) return 'Open payments';
    if (actionUrl.startsWith('/dashboard/invoices')) return 'Open invoices';
    if (actionUrl.startsWith('/dashboard/memberships')) return 'Open memberships';
    if (actionUrl.startsWith('/dashboard/estimates')) return 'Open estimates';
    if (actionUrl.startsWith('/dashboard/proposals')) return 'Open proposals';
    if (actionUrl.startsWith('/dashboard/contacts')) return 'Open contacts';
    if (actionUrl.startsWith('/dashboard/jobs')) return 'Open jobs';
    if (actionUrl.startsWith('/dashboard/past-due')) return 'Review past due';
    if (actionUrl.startsWith('/dashboard/recovery-renewals')) return 'Review queue';
    if (actionUrl.startsWith('/dashboard/recurring-billing')) return 'Review failures';
    if (actionUrl.startsWith('/dashboard/settings')) return 'Review failures';
  }
  switch (item.category) {
    case 'scheduling':
      return 'Open dispatch';
    case 'jobs':
      return 'Review job';
    case 'billing':
      return 'Review payment';
    case 'leads':
      return 'Open lead';
    case 'sales':
      return 'Open sale';
    case 'contacts':
      return 'Open contact';
    case 'team':
      return item.type === INTERNAL_MESSAGE_NOTIFICATION_TYPE ? 'Open chat' : 'Review team';
    case 'system':
      return 'Review';
  }
}

function groupNotificationsByPriority(
  items: readonly StaffNotification[],
): Record<NotificationPriority, StaffNotification[]> {
  return {
    action: items.filter((item) => item.priority === 'action'),
    attention: items.filter((item) => item.priority === 'attention'),
    fyi: items.filter((item) => item.priority === 'fyi'),
  };
}

function openNotificationTarget(
  actionUrl: string | null,
  navigate: ReturnType<typeof useNavigate>,
  onClose: () => void,
): void {
  if (!actionUrl) return;
  if (!isSafeNotificationActionUrl(actionUrl)) {
    toast.error('Could not open unsafe notification link');
    return;
  }
  onClose();
  if (actionUrl.startsWith('/')) {
    void navigate({ to: actionUrl as never });
    return;
  }
  try {
    const url = new URL(actionUrl);
    if (url.origin === window.location.origin) {
      void navigate({ to: `${url.pathname}${url.search}${url.hash}` as never });
      return;
    }
  } catch {
    // Leave malformed external targets to the browser.
  }
  window.location.assign(actionUrl);
}

function isSafeNotificationActionUrl(actionUrl: string | null | undefined): actionUrl is string {
  if (!actionUrl) return false;
  if (actionUrl.startsWith('/')) return !actionUrl.startsWith('//');
  try {
    const url = new URL(actionUrl);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return formatDateTimeInTimeZone(value, getViewerTimeZone(), {
    month: 'short',
    day: 'numeric',
  });
}

function formatNotificationWhen(item: StaffNotification): string {
  if (item.groupCount <= 1) return formatWhen(item.createdAt);
  const firstSeen = new Date(item.firstSeenAt);
  const lastSeen = new Date(item.lastEventAt);
  if (Number.isNaN(firstSeen.getTime()) || Number.isNaN(lastSeen.getTime())) {
    return `${item.groupCount} times`;
  }
  const timeZone = getViewerTimeZone();
  const firstDate = formatDateTimeInTimeZone(item.firstSeenAt, timeZone, {
    month: 'short',
    day: 'numeric',
  });
  const lastDate = formatDateTimeInTimeZone(item.lastEventAt, timeZone, {
    month: 'short',
    day: 'numeric',
  });
  if (firstDate !== lastDate) {
    return `${item.groupCount} times · ${firstDate}-${lastDate}`;
  }
  const relative = formatWhen(item.lastEventAt);
  return `${item.groupCount} times · last ${relative}${relative === 'now' ? '' : ' ago'}`;
}
